import { StorageKey } from "config/storage";

/**
 * Picks the implementation behind `@novorender/api` for this page load. The Vite
 * `novorender-api-engine-switch` plugin reads `isWasmEngineActive` before either
 * engine is imported, so keep its imports minimal. App code should not branch on
 * `isWasmEngineActive`: the engine is a drop-in, so detect what an API offers instead.
 */

export const WASM_ENGINE_QUERY_PARAM = "novolibWasm";

export enum Backend {
    Auto = "auto",
    WebGPU = "webgpu",
    WebGL2 = "webgl2",
}

export const BACKEND_OPTIONS = [
    { value: Backend.Auto, labelKey: "graphicsApiAuto" },
    { value: Backend.WebGPU, labelKey: "graphicsApiWebGpu" },
    { value: Backend.WebGL2, labelKey: "graphicsApiWebGl2" },
] as const;

/** `?novolibWasm=true|false` wins for the current page load only; otherwise the persisted setting decides. */
export function shouldUseWasmEngine(search: string, storedSetting: string | null): boolean {
    const param = new URLSearchParams(search).get(WASM_ENGINE_QUERY_PARAM);
    if (param === "true" || param === "false") {
        return param === "true";
    }
    return storedSetting === "true";
}

export function parseBackend(value: string | null): Backend {
    return Object.values(Backend).find((backend) => backend === value) ?? Backend.Auto;
}

function readLocalStorage(key: StorageKey): string | null {
    try {
        return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
        return null;
    }
}

export function getStoredBackend(): Backend {
    return parseBackend(readLocalStorage(StorageKey.ExperimentalEngineBackend));
}

/** Persisted settings as they were when this page loaded, i.e. what the running engine uses. */
export const startupEngineSettings = {
    enabled: readLocalStorage(StorageKey.ExperimentalEngine) === "true",
    backend: getStoredBackend(),
};

export const isWasmEngineActive = shouldUseWasmEngine(
    globalThis.location?.search ?? "",
    readLocalStorage(StorageKey.ExperimentalEngine),
);

export function isEngineReloadRequired(enabled: boolean, backend: Backend): boolean {
    return enabled !== startupEngineSettings.enabled || (enabled && backend !== startupEngineSettings.backend);
}

const ENGINE_BACKEND_LABELS: Record<string, string> = {
    [Backend.WebGPU]: "WebGPU",
    [Backend.WebGL2]: "WebGL2",
};

/** The graphics API bim-viewer-engine actually renders with, from its `view.statistics.engine.gpuBackend`. */
export function engineBackendLabel(statistics: unknown): string | undefined {
    const engine = (statistics as { engine?: { gpuBackend?: unknown } } | undefined)?.engine;
    const backend = engine?.gpuBackend;
    if (typeof backend !== "string" || backend === "") {
        return undefined;
    }
    return ENGINE_BACKEND_LABELS[backend] ?? backend;
}

/** The implementation this page loaded: the pinned dev server, or the in-app toggle. */
export function viewerImplementationLabel(
    pinned: "api" | "engine" | undefined,
    wasmEngineActive: boolean,
): "API" | "ENGINE" {
    if (pinned === "engine" || (pinned === undefined && wasmEngineActive)) {
        return "ENGINE";
    }
    return "API";
}

/** The implementation loaded by this page. */
export function activeViewerImplementation(): "API" | "ENGINE" {
    return viewerImplementationLabel(__NOVOROW_RENDERER__, isWasmEngineActive);
}

/**
 * Graphics API for the debug overlay.
 *
 * `@novorender/api` is WebGL2. The engine also renders with WebGL2, and it
 * reports that as `gpuBackend` once statistics arrive. Prefix the name with
 * the implementation so the two are not the same word. `gpuBackend` decides
 * for the engine, whichever host served the page.
 */
export function debugGraphicsLabel(implementation: "API" | "ENGINE", statistics: unknown): string | undefined {
    const backend = engineBackendLabel(statistics);
    if (backend !== undefined) {
        const name = backend === "WebGL 2" ? "WebGL2" : backend;
        return `engine:${name}`;
    }
    if (implementation === "API") {
        return "api:WebGL2";
    }
    return undefined;
}

/**
 * The benchmark overlay's renderer: `API:WebGL2`, `ENGINE:WebGL2`, or `ENGINE:WebGPU`.
 * The graphics API the view is actually using wins. Until it reports one, the
 * requested run and then the saved engine backend fill in.
 */
export function benchmarkRendererLabel(
    implementation: "API" | "ENGINE",
    statistics: unknown,
    requestedGraphics?: string | null,
): string {
    if (implementation === "API") {
        return "API:WebGL2";
    }
    const graphics =
        graphicsApiName(engineBackendLabel(statistics)) ??
        graphicsApiName(requestedGraphics) ??
        graphicsApiName(getStoredBackend());
    if (graphics === "WebGPU") {
        return "ENGINE:WebGPU";
    }
    if (graphics === "WebGL2") {
        return "ENGINE:WebGL2";
    }
    return "ENGINE";
}

function graphicsApiName(value: string | null | undefined): "WebGL2" | "WebGPU" | undefined {
    if (value === Backend.WebGPU || value === "WebGPU") {
        return "WebGPU";
    }
    if (value === Backend.WebGL2 || value === "WebGL2" || value === "WebGL 2") {
        return "WebGL2";
    }
    return undefined;
}
